import { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteOpportunityDocument,
  listOpportunityDocuments,
  uploadOpportunityDocument,
} from "../../api/clientOpportunityDocumentsApi";
import { resolveClientPortalAssetUrl } from "../../api/clientUploadApi";
import { formatClientDateTime } from "../../utils/clientPortalFormat";
import "../../styles/clientPortalUploads.css";

function formatFileSize(bytes) {
  if (!bytes) {
    return "";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function OpportunityDocumentsPanel({
  clientViewId,
  opportunityId,
  canEdit,
  intro = "Attach contracts, disclosures, HOA packets, and other files.",
}) {
  const inputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const body = await listOpportunityDocuments(clientViewId, opportunityId);
      setDocuments(body.documents || []);
    } catch (err) {
      setError(err.message || "Unable to load documents.");
    } finally {
      setLoading(false);
    }
  }, [clientViewId, opportunityId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setUploading(true);
    setError("");
    try {
      await uploadOpportunityDocument(clientViewId, opportunityId, file);
      await load();
    } catch (err) {
      setError(err.message || "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Remove ${doc.fileName}?`)) {
      return;
    }
    try {
      await deleteOpportunityDocument(clientViewId, opportunityId, doc.documentId);
      await load();
    } catch (err) {
      setError(err.message || "Unable to remove document.");
    }
  };

  return (
    <section className="panel-card cp-documents-panel">
      <div className="cp-documents-header">
        <div>
          <h3 className="opp-section-heading">Documents</h3>
          <p className="muted">{intro}</p>
        </div>
        {canEdit ? (
          <button
            type="button"
            className="btn btn-secondary"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? "Uploading…" : "+ Upload document"}
          </button>
        ) : null}
      </div>

      <input
        ref={inputRef}
        type="file"
        className="visually-hidden"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/jpeg,image/png,image/webp"
        onChange={handleUpload}
      />

      {error ? <p className="cp-upload-error">{error}</p> : null}
      {loading ? <p className="muted">Loading documents…</p> : null}

      {!loading && documents.length === 0 ? (
        <p className="muted">No documents attached yet.</p>
      ) : null}

      {!loading && documents.length > 0 ? (
        <ul className="cp-documents-list">
          {documents.map((doc) => (
            <li key={doc.documentId} className="cp-document-item">
              <div>
                <a
                  href={resolveClientPortalAssetUrl(doc.fileUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="cp-document-name"
                >
                  {doc.fileName}
                </a>
                <div className="muted cp-document-meta">
                  {formatClientDateTime(doc.createdAt)}
                  {doc.fileSize ? ` · ${formatFileSize(doc.fileSize)}` : ""}
                  {doc.uploadedByName ? ` · ${doc.uploadedByName}` : ""}
                </div>
              </div>
              {canEdit ? (
                <button
                  type="button"
                  className="cv-button-link cp-settings-danger-link"
                  onClick={() => handleDelete(doc)}
                >
                  Remove
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

export default OpportunityDocumentsPanel;
